<script>
  import Krpano from "./Krpano.svelte";
  import { onMount } from "svelte";
  import i18next from "i18next";
  import TourEmbed from "./TourEmbed.svelte";

  export let config;
  let configReceived = false;
  let configDone = false;
  let messageHandler;
  let embedConfig = null;
  let configPromise = null;
  let isCustom = false;

  // Set up message listener before fetching config
  onMount(() => {
    const isIframe = window !== window.parent;
    const urlParams = new URLSearchParams(window.location.search);
    const awaitsConfig = urlParams.get('awaitsConfig') === 'true';
    isCustom = urlParams.get('custom') === 'true';
    
    if (isIframe && awaitsConfig) {
      // Create a promise that resolves when config is received
      configPromise = new Promise((resolve) => {
        messageHandler = (event) => {          
          if (event.data?.type !== 'covecreek:config') {
            return;
          }
          
          embedConfig = event.data.data;
          configReceived = true;
          
          // Acknowledge receipt of config
          try {
            event.source.postMessage({ type: 'covecreek:config-received' }, '*');
          } catch (error) {
            console.warn('Failed to acknowledge config receipt:', error);
          }
          
          resolve();
        };

        window.addEventListener('message', messageHandler);

        // Set a timeout of 2 seconds to proceed with URL params if no postMessage
        setTimeout(() => {
          if (!configReceived) {
            // Extract config from URL parameters as fallback
            const urlConfig = {};
            for (const [key, value] of urlParams.entries()) {
              if (key !== 'awaitsConfig') {
                try {
                  // Try to parse as JSON first
                  urlConfig[key] = JSON.parse(value);
                } catch {
                  // If not JSON, handle arrays and other values
                  if (value.includes(',')) {
                    urlConfig[key] = value.split(',');
                  } else {
                    urlConfig[key] = value;
                  }
                }
              }
            }
            if (Object.keys(urlConfig).length > 0) {
              embedConfig = urlConfig;
              configReceived = true;
            }
            resolve();
          }
        }, 2000);
      });

      // Wait for config or timeout before setting configDone
      configPromise.then(() => {
        configDone = true;
      });
    } else {
      // Not in iframe or not awaiting config, proceed immediately
      configDone = true;
    }

    return () => {
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
      }
    };
  });

  const fetchConfig = async () => {
    // If we're awaiting config, wait for it first
    if (configPromise) {
      await configPromise;
    }

    while (!config) {
      try {
        const response = await fetch("./build/tour-config.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const baseConfig = await response.json();
        
        // If we have embed config, merge it now
        if (embedConfig) {
          // Handle scene filtering if sceneIds provided
          if (embedConfig.sceneIds) {
            baseConfig.scenes = baseConfig.scenes.filter(scene => 
              embedConfig.sceneIds.includes(scene.id)
            );
            delete embedConfig.sceneIds;
          }
          
          // Merge the rest of the embed config
          config = {
            ...baseConfig,
            ...embedConfig
          };
        } else {
          config = baseConfig;
        }
        
      } catch (error) {
        console.error(
          "Unable to fetch tour config. Retrying...",
          error,
        );
        await new Promise((resolve) => setTimeout(resolve, 3000)); // Retry after 3 seconds
      }
    }
  };

  onMount(async() => {
    await fetchConfig();
    // set up i18n
    i18next.changeLanguage(config.language || 'en');
  });
</script>

<!-- /*____________ Markup ____________*/ -->
<main>
  {#if config && configDone}
    <div class="tour-embed">
      <TourEmbed tour={config} custom={isCustom} />
    </div>
  {/if}
</main>

<!-- /*____________ CSS ____________*/ -->
<style>
  main {
    padding: 0;
    margin: 0;
    width: 100vw;
    height: 100vh;
  }

  .tour-embed {
    width: 100%;
    height: 100%;
  }

  :global(html, body) {
    overflow: hidden;
    width: 100%;
    height: 100%;
    margin: 0;
  }

  :global(.no-select) {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
</style>
